<docs>
The Zone data table: toolbar (search + filters), sortable headers, selectable rows with a
batch-action bar, collapsible nested rows, loading/empty states and pagination. Two modes: `local`
(the default) does search, filtering, sorting and pagination over the `rows` you hand it; `server`
hands that work to a single `fetcher` function that receives the whole table state on every change.
Also exports the TableColumn / TableRow / TableFilter / TableFilterType / TableFilterPlacement /
TableFilterValue / TableSort / TableQueryState / TableFetchResult / TableBatchAction /
TableSelection / TableCrossPageSelection / RowClassContext types.

## Columns

`columns` is the only required prop. Each `TableColumn`:

- `key` reads `row[key]` for the cell value and names the `#cell-KEY` slot; `label` is the header text.
- `sortable` shows the sort control (single-column sort: asc, then desc, then off); `tooltip` an info
  icon + hover tooltip in the header; `searchable` (default true) set to false keeps the column out
  of the search.
- `formatter(value, row)` is applied to the default cell rendering; `sortAccessor(row)` returns the
  comparable for local sorting on something other than `row[key]`.
- `component` is a Vue component rendered as the cell, given `{ row, value, column }` props.
- `width` / `minWidth` / `align` / `cellClass` / `headerClass` are Tailwind classes.

## Modes

`mode="local"` (default): the component filters/sorts/paginates `rows` itself. `mode="server"`: the
component renders `rows` verbatim and never touches them. Supply `fetcher` and it owns the data; or
omit `fetcher`, pass `rows` + `totalCount` (the page the server rendered) and answer `query-change`
with the next request.

## Fetcher

One function serves sorting, filtering, pagination and search: it is called with the whole state
every time any part of it changes, so the backend always gets the full picture.
`fetcher(state: TableQueryState)` where state is `{ page, perPage, sort: { key, direction } | null,
search, filters }`; `filters` is `{ [filterKey]: (string | number)[] }`, empty filters omitted. Return
`{ rows, totalCount }` or a bare row array, sync or async. Rejections are emitted as `fetch-error`.
While in flight (a `fetcher` call, or `loading` from the caller) the skeleton stands in for the rows
still in state, one row each and as tall as they were, so the table keeps its height; a first load,
with nothing to show yet, draws `skeletonRows` placeholder rows.

```ts
async function loadMembers({ page, perPage, sort, search, filters }) {
  const { data } = await axios.get('/members', {
    params: { page, per_page: perPage, sort: sort ? `${sort.key},${sort.direction}` : null, q: search, ...filters },
  });

  return { rows: data.members, totalCount: data.total_count };
}
```

## v-model

Every piece of table state is optional two-way. Pass nothing and the table owns it; bind it and you
control it: `v-model:page`, `v-model:perPage`, `v-model:sort`, `v-model:search`,
`v-model:filterValues`, `v-model:selected`, `v-model:expandedKeys`.

## Filters

`filters` is `{ key, label, type?, placement?, options?, predicate?, loading? }[]`. Three placements,
all writing to one `filterValues` bag:

- `placement: 'drawer'` (default): behind the Filters button, in a right-hand ZoneDrawer.
- `placement: 'panel'`: behind the same button, in an anchored floating menu instead.
- `placement: 'inline'`: in the toolbar row as a ZonePillGroup, for the one or two column toggles
  that should always be visible. Untouched by Reset. An inline filter whose labels carry counts sets
  `loading: true` while the request that refreshes them is in flight: its pills turn into empty
  outlines, the way the page skeletons draw them, and come back with the new labels.

`drawer` and `panel` are the same content in different chrome, and a table gets one of them: there is
a single Filters trigger, and a button can only open one thing. Set `placement: 'panel'` on all of a
table's non-inline filters to get the floating menu; anything else resolves to the drawer. The
trigger carries a count badge (clamped at 99+); Reset appears only while something is active and
clears the non-inline filters; each filter also has its own Clear.

`type` is `'multiselect'` (default), `'select'` or `'toggle'`. Whatever the control, the stored value
is always an array (`select` holds one element, `toggle` holds `[true]`), so the count and the
fetcher never branch on filter type.

`predicate(row, values)` replaces the default "row[key] is one of values" for local matching. The
default is set membership, so it cannot express a threshold, a range or a date window: a control
writing `['50']` for a "minimum hours" filter is compared for equality against the row's value and
matches nothing. Give such a filter a predicate. Ignored in server mode, where the fetcher does the
filtering. There is no `daterange` control (Zone has no calendar-range component): use `#filters`
for the UI and a `predicate` for the matching. A filter declared without `options` renders no
control of its own on the surface, so `#filters` is where its UI lives; the declaration still
counts for the badge and is cleared by Reset.

## Nested rows

Rows nest via `row.children` (rename with `childrenKey`). Rows with children get a chevron in the
first column; children are indented and hidden until expanded. Search and filters keep a parent whose
descendant matches, and auto-expand it. Pagination counts top-level rows only.

## Selection

`selectable` gives a flat array of row keys via `v-model:selected`. `crossPageSelectable` switches to
the "all, minus these" model: the header checkbox means every row behind the current query, and
`selected` round-trips as `{ all: true, except: [...] }`, a shape a flat array cannot express since
it covers rows on pages that were never fetched. `batchTotal` supplies the count the bar shows; the
"Select all (N)" link is hidden in this mode, the header checkbox already is it. `rowSelectable(row)`
vetoes individual rows: it disables that row's checkbox and is honoured by select-all and
select-all-across-pages, but does not filter keys the caller put in `selected` (dropping ids someone
set on purpose hides bugs). Under `crossPageSelectable` it can only speak for rows the client has
fetched, so the server has to enforce the same rule across the rest.

## Slots

`#cell-KEY` is the idiomatic way to render anything custom (avatar cells, pills, an actions column
with a ZoneButtonDropdown); prefer it over `column.component`, which exists for config-driven tables
built from plain data. `#header-KEY` replaces a header. Because these slot names are dynamic they
cannot be re-declared on a part: every slot this table receives is relayed to the parts, and each
part renders the ones it knows about. The empty state is customised piecewise: `emptyTitle` /
`emptyDescription` / `emptyActionLabel` for the copy and one button, `#empty-actions` for more than
one button, `#empty-image` for the illustration, `#empty` for a whole different layout.

## Exposed

`reload()` re-runs the `fetcher` with the current query state: the way to refresh a server-mode
table after a mutation (an archive, a delete), or to offer a Retry after `fetch-error`.

## Events

`query-change` fires on any change the user makes, in both modes, and never on mount (the starting
state is the one the props gave): use it to ask the server for that state, or to mirror it into the URL.
`scroll` is any scroll in the document, so callers can dismiss overlays they render inside cells;
this component already closes its own on the same signal.

## Anatomy

This file is the component and the whole public contract: props, events, slots, types, query state
and the row pipeline. The markup lives in parts under `zone/components/base/table/`, which are internal (no stories,
not in llms.txt, never imported by anything but this file, free to change shape): `Toolbar.vue`
(search field, Filters trigger, inline filter pills, `#toolbar-actions`) with `Filters.vue` (the
trigger's surface: a side drawer or an anchored floating menu) and `FilterFields.vue` (one labelled
control per filter + the `#filters` slot); `BatchBar.vue` (the "N selected" bar, its actions and
its ⋮ overflow menu); `Head.vue` (`<thead>`: master checkbox, labels, tooltips, sort controls);
`Body.vue` (`<tbody>`: skeleton, empty state, rows, cells, nesting chevron); `Scrollbar.vue` (Zone's
own horizontal scrollbar row); `Pagination.vue` (the footer: summary, per-page, page strip). Shared
modules: `cells.ts` (the class rules Head and Body share, so a `<th>` and its `<td>` agree),
`filters.ts` (reading and writing the filter bag), `query.ts` (the comparator and the search/filter
predicates), `types.ts` (every exported type, re-exported from this file).

The parts are presentational: props down, events up, no query state of their own. That keeps this
file the single owner of state and the single emitter to the caller, so `v-model:page` can never be
half-implemented by a part. Two exceptions, both because the state is about a widget rather than the
data: BatchBar owns its overflow menu's open state (Table closes it through `closeMenu()` on a
document scroll) and Scrollbar owns its own measurements (Table drives its lifecycle and delegates
`syncScrollbar`).

## Engine

The row pipeline is TanStack Table v9 (`@tanstack/vue-table`): filter with descendant propagation,
recursive sort, expansion flattening, then a page slice that counts top-level rows only. Four
features are registered (rowSorting, columnFiltering, rowExpanding, rowPagination), each with its
`create*RowModel()`. What TanStack does not own, and why:

- The comparator. `compareValues` (blanks last, numeric-string coercion) and `sortAccessor` are
  injected as an inline `sortFn`; no built-in sortFn matches.
- The predicates. Search reads the formatted value (so `formatter` output is searchable), honours
  `searchable: false`, and a filter key need not be a column. All of it runs as one inline `filterFn`
  on a hidden `__zone_query__` column, which also walks ancestors so a matching parent keeps its
  whole subtree.
- The sort cycle: asc, desc, off, single column, always. TanStack's own toggle is data-type dependent
  and never yields the `null` this component emits.
- Selection. Not `rowSelectionFeature`: its state is keyed by stringified row ids, while this
  component's API is an array of the caller's raw keys.
- Rendering. Cells come from the `columns` prop and the slots, not from `columnDef.cell` /
  `getVisibleCells()` / FlexRender.

`useTable` is a composable that reads every option the moment it is called, so the refs it depends
on are declared before it in the script (see the comment there).

## Scroll

The table body scrolls horizontally on its own, under Zone's own scrollbar row (the Figma "Scroll"
anatomy element): an 8px gray-400 thumb that appears only while the content overflows. The native
bar is hidden with `no-scrollbar`; native panning (trackpad, shift-wheel, keyboard) still works, and
the custom thumb is a second handle onto the same scrollLeft. Nothing to configure: it measures
itself.

## Built on

Zone components, not hand-rolled markup, so the table inherits their spec: ZoneSearchInput (toolbar
search), ZoneButton (Filters trigger, batch actions, empty-state action), ZonePill (the "N selected"
chip and its clear ×), ZonePillGroup (inline filters), ZoneSelect (panel filters, per-page),
ZoneCheckbox (selection), Reka Popover / DropdownMenu / Pagination.

## Notes

- Row identity comes from `rowKey` (default `id`) and must be unique across the whole tree, nested
  rows included: it keys selection and expansion.
- Selection survives search/filter/pagination on purpose: narrowing the view does not drop the ids
  you already picked.
- The batch bar shows the first 3 actions inline and the rest under a "more" menu; when the bar is
  too narrow for three, actions move under the menu one at a time until they fit. The Zone
  guideline is at most 10 actions, and to give every one an icon.
- An expanded row takes the hover background; its children stay white. Per the Zone guideline the
  tint marks "this group is open", so tinting the children too would make a collapsed parent and an
  open subtree read as the same thing.
- Filters popover, the filters drawer, the batch overflow menu and the per-page ZoneSelect all
  teleport to `#zone-teleport` (carries `.w-tw`): query `document.body` in tests, not the wrapper. A
  table with filters must also be mounted with `attachTo: document.body` in specs: the drawer is a
  Reka Drawer, and DrawerContentImpl looks its title up with `document.getElementById`, which finds
  nothing in a detached tree.
- `stickyHeader` needs `maxHeight` to do anything. The scroll container is already a scroll container
  in both axes (`overflow-x-auto` forces `overflow-y: auto`), so with no height cap it is as tall as
  its content and a sticky header never leaves the top.
- Every `<tr>` paints an opaque background, including the header, skeleton and empty-state rows. The
  pinned-column cells are `bg-inherit` so they pick up a row's hover/expanded tint, which means an
  unpainted row makes them transparent and columns scrolling under a pinned column show straight
  through it.
- Do not dim a row with `opacity` on the `<tr>` via `rowClass`: it fades the cell borders with the
  content, so the grid lines go patchy, and it makes the row a stacking context, which breaks the
  pinned column's layering. Dim the cell contents instead: every body cell wraps its content in one
  element, so `[&_td>*]:opacity-50` reaches all of them and leaves the strokes alone.
- Any scroll closes the filters panel and the batch overflow menu (capture-phase listener on
  `document`, because `scroll` does not bubble). Scrolling inside an overlay is exempt. The per-page
  ZoneSelect is not covered: ZoneSelect owns its open state internally and has no `open` prop to
  drive. The batch overflow menu is a raw Reka DropdownMenu rather than ZoneButtonDropdown for
  exactly that reason, though it borrows its styling.
- The sort control appears on hover and stays put once the column is sorted, per the Zone guideline.
  Sorting is single-column: picking another column drops the first. In server mode "off" emits
  `sort: null`; it is on the caller to decide what no-sort means to their endpoint.
- Nested rows are plain `<tr>`s, not Reka Collapsible: CollapsibleContent renders a `<div>`, which is
  invalid inside `<tbody>`.
- The header select-all checkbox shows a partial page selection through ZoneCheckbox's own
  `indeterminate` prop, which owns the DOM-property plumbing.
- The empty state's default illustration is Zone's empty-datatable artwork, inlined as SVG (it lives
  in the Sprockets asset tree, which Vite cannot resolve from here). `emptyImage` or `#empty-image`
  replaces it.

Figma: [blueprint](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=4110-55783&m=dev),
[guidelines](https://www.figma.com/design/eOrg3dHaQFZ7VdCZCcomNC/-Zone-DS--Components-Specs?node-id=4017-18553&m=dev),
[atoms](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=4876-56420&m=dev),
[molecules](https://www.figma.com/design/DD0eEumGwzGfrPbb7q2iak/-Zone--Tokens---Components?node-id=4227-20671&m=dev)
</docs>

<template>
  <div
    class="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
    data-testid="zone-table"
  >
    <TableToolbar
      v-if="showToolbar"
      :searchable="searchable"
      :search-input="searchInput"
      :search-placeholder="searchPlaceholder"
      :surface-filters="surfaceFilters"
      :inline-filters="inlineFilters"
      :values="internalFilters"
      :has-filter-surface="hasFilterSurface"
      :filter-surface="filterSurface"
      :filters-open="filtersOpen"
      :active-filter-count="activeFilterCount"
      :active-filter-badge="activeFilterBadge"
      :set-filter="setFilter"
      :reset="clearFilters"
      @update:search-input="onSearchInput"
      @update:filters-open="filtersOpen = $event"
      @reset="clearFilters"
      @set="setFilter"
    >
      <template
        v-for="name in forwardedSlots"
        :key="name"
        #[name]="slotProps"
      >
        <slot
          :name="name"
          v-bind="slotProps ?? {}"
        />
      </template>
    </TableToolbar>

    <TableBatchBar
      ref="batchBar"
      :visible="showBatchBar"
      :selected-count-text="selectedCountText"
      :batch-actions="batchActions"
      :can-select-all-across-pages="canSelectAllAcrossPages"
      :batch-total-text="batchTotalText"
      @clear="clearSelection"
      @select-all-across-pages="selectAllAcrossPages"
      @action="runBatchAction"
    />

    <!--
      `no-scrollbar` hides the NATIVE bar so Zone's own scrollbar row below is the single visible
      one, exactly as Zone::DataTable does on the Rails side. The container still scrolls
      natively, so trackpad / shift-wheel / keyboard panning keep working untouched.
    -->
    <div
      ref="scrollContainer"
      :class="scrollContainerClasses"
      :style="maxHeight ? { maxHeight } : undefined"
      data-testid="table-scroll-container"
      @scroll="syncScrollbar"
    >
      <table
        class="w-full border-separate border-spacing-0 text-sm"
        :aria-label="label || undefined"
      >
        <!--
          `#header-KEY` slots are relayed rather than re-declared: a dynamic slot name cannot be
          declared twice, so every slot this table was given is forwarded and Head.vue renders the
          `header-*` ones. Body.vue gets the same treatment for `cell-*`.
        -->
        <TableHead
          :columns="columns"
          :sort="internalSort"
          :selectable="selectable"
          :all-selected="allVisibleSelected"
          :some-selected="selectAllIndeterminate"
          :select-all-disabled="selectAllDisabled"
          :sticky-header="stickyHeader"
          :sticky-options="stickyOptions"
          @toggle-all="toggleAll"
          @toggle-sort="toggleSort"
        >
          <template
            v-for="name in forwardedSlots"
            :key="name"
            #[name]="slotProps"
          >
            <slot
              :name="name"
              v-bind="slotProps ?? {}"
            />
          </template>
        </TableHead>

        <TableBody
          :columns="columns"
          :rows="flatRows"
          :is-loading="isLoading"
          :skeleton-rows="skeletonRows"
          :column-count="columnCount"
          :selectable="selectable"
          :has-nested-rows="hasNestedRows"
          :sticky-options="stickyOptions"
          :row-class="rowClass"
          :is-selected="isSelected"
          :can-select-row="canSelectRow"
          :empty-title="emptyTitle"
          :empty-description="emptyDescription"
          :empty-action-label="emptyActionLabel"
          :empty-image="emptyImage"
          @row-click="$emit('row-click', $event)"
          @toggle-row="toggleRow"
          @toggle-expanded="toggleExpanded"
          @empty-action="$emit('empty-action')"
        >
          <template
            v-for="name in forwardedSlots"
            :key="name"
            #[name]="slotProps"
          >
            <slot
              :name="name"
              v-bind="slotProps ?? {}"
            />
          </template>
        </TableBody>
      </table>
    </div>

    <!--
      Zone's own horizontal scrollbar row. It measures the scroll container above rather than
      owning one, so it is handed the element; see table/Scrollbar.vue.
    -->
    <TableScrollbar
      ref="scrollbar"
      :get-container="getScrollContainer"
    />

    <TablePagination
      v-if="paginated"
      :page="internalPage"
      :per-page="internalPerPage"
      :per-page-options="perPageOptions"
      :page-count="pageCount"
      :total-items="totalItems"
      :items-label="itemsLabel"
      @update:page="setPage"
      @update:per-page="setPerPage"
    />
  </div>
</template>

<script lang="ts">
import { computed, onBeforeUnmount, onMounted, onUpdated, ref, watch, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  tableFeatures,
  columnFilteringFeature,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSortingFeature,
  createExpandedRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  type ColumnDef,
  type Row as TanStackRow,
  useTable,
} from '@tanstack/vue-table';
import type {
  TableRow,
  FlatRow,
  RowClassContext,
  TableBatchAction,
  TableColumn,
  TableCrossPageSelection,
  TableFetcher,
  TableFilter,
  TableFilterPlacement,
  TableFilterValue,
  TableQueryState,
  TableQueryValue,
  TableRowKey,
  TableSelection,
  TableSort,
} from './table/types';
import TableScrollbar from './table/Scrollbar.vue';
import TablePagination from './table/Pagination.vue';
import TableBatchBar from './table/BatchBar.vue';
import TableHead from './table/Head.vue';
import TableBody from './table/Body.vue';
import TableToolbar from './table/Toolbar.vue';
import { isFilterActive as filterIsActive } from './table/filters';
import {
  type StickyOptions,
  type StickyRegion,
  stickyColumnClasses as stickyColumnClassesFor,
} from './table/cells';
import { cloneSelection, compareValues, formatCount, matchesFilters, matchesSearch } from './table/query';

/** The slice of table/Scrollbar.vue's instance this component drives. */
interface ScrollbarPart {
  observe: () => void;
  teardown: () => void;
  scheduleSync: () => void;
  /** Async: it awaits a tick when the bar goes from hidden to shown. See `sync()` in Scrollbar.vue. */
  sync: () => Promise<void>;
}

/**
 * PUBLIC TYPES. The declarations live in `./table/types` — every part under that directory needs
 * them, and importing them back out of this file would make each part circular with its parent —
 * but `zone/components/base/Table.vue` stays the documented import path, so they are re-exported verbatim here.
 * Adding a type means adding it in BOTH places; TableApi.spec.ts fails if one of these stops
 * resolving.
 */
export type {
  RowClassContext,
  TableBatchAction,
  TableColumn,
  TableCrossPageSelection,
  TableFetcher,
  TableFetchResult,
  TableFilter,
  TableFilterPlacement,
  TableFilterType,
  TableFilterValue,
  TableQueryState,
  TableRow,
  TableRowKey,
  TableSelection,
  TableSort,
} from './table/types';

/**
 * The TanStack Table v9 engine.
 *
 * Module scope on purpose: `features` is pinned when the table is constructed (every later
 * `setOptions` re-reads it from the ORIGINAL options), so a ref or computed here would be
 * silently ignored.
 *
 * A feature module only contributes state and API methods — the `*RowModel` factories are what
 * actually move rows. Registering a feature but omitting its row model is a SILENT no-op: the
 * state flips and nothing else happens.
 *
 * Four features, deliberately. `globalFilteringFeature`, `rowSelectionFeature` and
 * `columnVisibilityFeature` are NOT registered; see the ENGINE notes in the guide above.
 * No `sortFns` / `filterFns` registries either — every comparator and predicate is passed as an
 * inline function on the column def, which needs no registration.
 */
const features = tableFeatures({
  rowSortingFeature,
  columnFilteringFeature,
  rowExpandingFeature,
  rowPaginationFeature,

  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
});

type ZoneFeatures = typeof features;
type ZoneRow = TanStackRow<ZoneFeatures, TableRow>;
type ZoneColumnDef = ColumnDef<ZoneFeatures, TableRow, unknown>;

/**
 * Id of a synthetic column that never renders. Search and every value filter are evaluated by ONE
 * filter function hung off this column, so `filters[].key` does not have to name a real column
 * (TanStack silently drops a `columnFilters` entry whose id has no column) and so search and
 * filters stay a single conjoined predicate — which is what the keep-the-ancestors rule applies to.
 */
const QUERY_COLUMN_ID = '__zone_query__';
</script>

<script setup lang="ts">
defineOptions({ name: 'ZoneTable' });

const { t } = useI18n();

const props = defineProps({
  /** The only required prop; see the Columns section. */
  columns: {
    type: Array as PropType<TableColumn[]>,
    required: true,
  },
  /** The data. Local mode filters, sorts and paginates them; server mode renders them verbatim. */
  rows: {
    type: Array as PropType<TableRow[]>,
    default: () => [],
  },
  /** Property on each row holding its unique id. Must be unique across the whole nested tree. */
  rowKey: {
    type: String,
    default: 'id',
  },
  /** Property on each row holding its child rows. */
  childrenKey: {
    type: String,
    default: 'children',
  },
  /** See the Modes section. */
  mode: {
    type: String as PropType<'local' | 'server'>,
    default: 'local',
    validator: (v: string) => ['local', 'server'].includes(v),
  },
  /**
   * Server mode: one function for sorting, filtering, pagination and search. See the Fetcher
   * section.
   */
  fetcher: {
    type: Function as PropType<TableFetcher>,
    // `undefined`, not `null`: Vue keeps the prop optional in the type, so `if (props.fetcher)`
    // narrows instead of reading as always-true.
    default: undefined,
  },
  /** Server mode without a fetcher: the total row count behind the current page. */
  totalCount: {
    type: Number,
    default: null,
  },
  /** Show the skeleton instead of the rows (a `fetcher` in flight does this by itself). */
  loading: {
    type: Boolean,
    default: false,
  },
  /** How many placeholder rows a first load draws; a reload draws one skeleton row per row on screen, as tall. */
  skeletonRows: {
    type: Number,
    default: 10,
  },
  /** Accessible name of the `<table>`. */
  label: {
    type: String,
    default: null,
  },
  /** null (default) shows the toolbar only when it would have content in it. */
  toolbar: {
    type: Boolean,
    default: null,
  },
  /** The toolbar search field. */
  searchable: {
    type: Boolean,
    default: true,
  },
  /** Placeholder of the toolbar search field. */
  searchPlaceholder: {
    type: String,
    default: null,
  },
  /** The search query, as `v-model:search`. */
  search: {
    type: String,
    default: '',
  },
  /**
   * Milliseconds to wait after the last keystroke before the search is committed (and, in server
   * mode, refetched). The input itself stays uncontrolled-fast; only the commit is delayed.
   * 0 commits synchronously.
   */
  searchDebounce: {
    type: Number,
    default: 300,
  },
  /** See the Filters section. */
  filters: {
    type: Array as PropType<TableFilter[]>,
    default: () => [],
  },
  /** The filter bag, as `v-model:filterValues`: `{ [filterKey]: values[] }`. */
  filterValues: {
    type: Object as PropType<Record<string, TableFilterValue[]>>,
    default: () => ({}),
  },
  /** The active sort, as `v-model:sort`; `null` is unsorted. */
  sort: {
    type: Object as PropType<TableSort | null>,
    default: null,
  },
  /** `false` renders every row on one page and drops the footer. */
  paginated: {
    type: Boolean,
    default: true,
  },
  /** The current page, as `v-model:page`. */
  page: {
    type: Number,
    default: 1,
  },
  /** Rows per page, as `v-model:perPage`. */
  perPage: {
    type: Number,
    default: 10,
  },
  /** Choices offered by the per-page select in the footer. */
  perPageOptions: {
    type: Array as PropType<number[]>,
    default: () => [10, 25, 50, 100],
  },
  /** Noun used in the "Showing 1-10 of 1,234 items" summary. Counts are grouped with commas. */
  itemsLabel: {
    type: String,
    default: 'items',
  },
  /**
   * A checkbox per row; the keys come back through `v-model:selected`. See the Selection section.
   */
  selectable: {
    type: Boolean,
    default: false,
  },
  /**
   * Switches selection to the "all, minus these" model: the header checkbox means EVERY row
   * matching the current query, not just the visible page, and `selected` round-trips as
   * `{ all: true, except: [...] }`. Rows on pages that were never fetched are covered, which a
   * flat key array cannot express. `batchTotal` supplies the count the batch bar shows.
   */
  crossPageSelectable: {
    type: Boolean,
    default: false,
  },
  /**
   * The selection, as `v-model:selected`: row keys, or `{ all: true, except: [...] }` under
   * `crossPageSelectable`.
   */
  selected: {
    type: [Array, Object] as PropType<TableSelection>,
    default: () => [],
  },
  /**
   * Per-row veto on selection. Gates the row's checkbox and every bulk helper (select-all,
   * select-all-across-pages), but deliberately does NOT filter keys the caller put in `selected`
   * itself — silently dropping ids a caller set on purpose is worse than showing them.
   *
   * Under `crossPageSelectable` this can only speak for rows the client has actually fetched;
   * the server still has to enforce the same rule over the rest of the collection.
   */
  rowSelectable: {
    type: Function as PropType<(row: TableRow) => boolean>,
    default: null,
  },
  /**
   * Extra classes per row. Returns anything Vue's class binding accepts — a string, an array, or
   * an object for conditional classes. The context argument carries render state (`expanded`,
   * `selected`, …) that isn't on the row itself.
   */
  rowClass: {
    type: Function as PropType<(row: TableRow, context: RowClassContext) => unknown>,
    default: null,
  },
  /**
   * Actions offered by the "N selected" bar; the first 3 inline (fewer when the bar is too narrow
   * for them), the rest under a "more" menu.
   */
  batchActions: {
    type: Array as PropType<TableBatchAction[]>,
    default: () => [],
  },
  /**
   * Total selectable rows behind the CURRENT query, across every page — the count in
   * "Select all (N)". Only needed in server mode where a page of rows says nothing about the
   * size of the whole; left unset it falls back to the table's own `totalItems`, which is
   * already the right answer for every local table. See `effectiveBatchTotal`.
   */
  batchTotal: {
    type: Number,
    default: null,
  },
  /** Keys of the expanded parent rows, as `v-model:expandedKeys`. */
  expandedKeys: {
    type: Array as PropType<TableRowKey[]>,
    default: () => [],
  },
  /**
   * Pins the header row while the body scrolls under it. Needs something to stick TO: this
   * container is already a scroll container (its `overflow-x-auto` forces `overflow-y` to
   * `auto` too), so with no height bound it is as tall as its content and a sticky header never
   * separates from the top. Pair it with `maxHeight`.
   */
  stickyHeader: {
    type: Boolean,
    default: false,
  },
  /**
   * A CSS length capping the scrolling body's height, applied INLINE (e.g. '420px', '60vh') —
   * inline because it is a value, not one of a fixed set of classes Tailwind could have
   * generated. This is what turns `stickyHeader` from a no-op into a fixed header row.
   */
  maxHeight: {
    type: String,
    default: null,
  },
  /** Pins the first column (and the selection checkbox) while the body scrolls horizontally. */
  stickyFirstColumn: {
    type: Boolean,
    default: false,
  },
  /** Title of the empty state. */
  emptyTitle: {
    type: String,
    default: 'Oops! nothing here',
  },
  /** Copy under the empty-state title. */
  emptyDescription: {
    type: String,
    default: null,
  },
  /** Renders a button in the empty state that emits `empty-action`. */
  emptyActionLabel: {
    type: String,
    default: null,
  },
  /** img src replacing the default empty-datatable illustration (or the `#empty-image` slot). */
  emptyImage: {
    type: String,
    default: null,
  },
});

const emit = defineEmits<{
  'update:page': [page: number];
  'update:perPage': [perPage: number];
  /** `null` is unsorted; in server mode it is on the caller to decide what no-sort means to their endpoint. */
  'update:sort': [sort: TableSort | null];
  'update:search': [search: string];
  'update:filterValues': [values: Record<string, TableFilterValue[]>];
  'update:selected': [selected: TableSelection];
  'update:expandedKeys': [keys: TableRowKey[]];
  /** Any state change, in both modes. Use it to mirror table state into the URL. */
  'query-change': [state: TableQueryState];
  /** Any scroll in the document, so callers can dismiss overlays they render inside cells. */
  scroll: [event: Event];
  /** A row was clicked. */
  'row-click': [row: TableRow];
  /** A batch-bar action was chosen, with the selection it applies to. */
  'batch-action': [payload: { action: TableBatchAction; selected: TableSelection }];
  /** The "Select all (N)" link of the batch bar was clicked. */
  'select-all-across-pages': [];
  /** The empty-state button (`emptyActionLabel`) was clicked. */
  'empty-action': [];
  /** The `fetcher` rejected. */
  'fetch-error': [error: unknown];
}>();

const slots = defineSlots<{
  /** Right side of the toolbar (export button, column picker). */
  'toolbar-actions'?(): any;
  /**
   * Extra controls inside the filters surface, for anything the declarative model cannot express (dependent
   * options, date ranges).
   */
  filters?(props: {
    values: Record<string, TableFilterValue[]>;
    setFilter: (key: string, values: TableFilterValue[] | null) => void;
    reset: () => void;
  }): any;
  /** Replaces the empty state. */
  empty?(): any;
  /** Replaces just the empty state's illustration. */
  'empty-image'?(): any;
  /**
   * Replaces the empty state's single `emptyActionLabel` button with your own buttons (two CTAs, a
   * link), under the title and description of the default layout.
   */
  'empty-actions'?(): any;
  /** A column's cells: the row, the cell value, the column, and the nesting state with `toggle` to expand/collapse. */
  [cell: `cell-${string}`]: (props: {
    row: TableRow;
    value: unknown;
    column: TableColumn;
    index: number;
    level: number;
    expanded: boolean;
    toggle: () => void;
  }) => any;
  /** A column's header. */
  [header: `header-${string}`]: (props: { column: TableColumn; sort: TableSort | null }) => any;
}>();

// Template refs. `scrollbar` is table/Scrollbar.vue, typed once here because this component
// drives its lifecycle from four hooks.
const batchBar = ref<{ closeMenu: () => void } | null>(null);
const scrollContainer = ref<HTMLElement | null>(null);
const scrollbar = ref<ScrollbarPart | null>(null);

// ── table-facing state ──────────────────────────────────────────────────────────────────────────
//
// `useTable` installs watchers with `immediate: true` the moment it is called, which read every
// option once, right then — so everything it depends on has to exist as a ref BEFORE that call.

const internalPage = ref(props.page);
const internalPerPage = ref(props.perPage);
const internalSort = ref<TableSort | null>(props.sort);
const internalSearch = ref(props.search);
const internalFilters = ref<Record<string, TableFilterValue[]>>({ ...props.filterValues });
const internalExpanded = ref<TableRowKey[]>([...props.expandedKeys]);
const fetchedRows = ref<TableRow[]>([]);
const fetchedTotal = ref<number | null>(null);

const searchPlaceholder = computed<string>(() => props.searchPlaceholder ?? t('search_placeholder'));
const isServerMode = computed(() => props.mode === 'server');

/** Rows the caller gave us, or the ones our own fetcher loaded. */
const sourceRows = computed<TableRow[]>(() => (
  isServerMode.value && props.fetcher ? fetchedRows.value : props.rows
));

// TanStack memoizes its core row model on the IDENTITY of `options.data` (`!==`), so a caller
// mutating in place — `rows.push(...)`, `splice`, `rows[0] = …`, or a nested `children` array —
// would leave the table frozen on its first row model while still re-rendering, i.e. silently
// stale. A deep watcher bumps a version counter, and `tableData` hands TanStack a fresh array
// identity whenever anything in the tree changes. `.slice()` alone is not enough: it would only
// cover top-level pushes, not nested children. `rowKey`/`childrenKey` are watched too, since
// changing either re-shapes the whole tree.
// Cost: `deep` walks the row tree on each change. If a caller ever has enough rows for that to
// hurt, the fix is to hand `rows` a new array identity instead — not to drop this watcher.
const dataVersion = ref(0);

watch(
  () => [sourceRows.value, props.rowKey, props.childrenKey],
  () => { dataVersion.value += 1; },
  { deep: true, flush: 'sync' },
);

const tableData = computed<TableRow[]>(() => {
  // Read the counter purely to register it as a dependency — `sourceRows` is mutated in
  // place, so bumping `dataVersion` is the only thing that invalidates this computed.
  // eslint-disable-next-line no-void -- discarding the read is the point
  void dataVersion.value;

  return sourceRows.value.slice();
});

const activeFilters = computed(() => Object.entries(internalFilters.value)
  .reduce((acc, [key, values]) => {
    if (Array.isArray(values) && values.length) {
      acc[key] = values;
    }

    return acc;
  }, {} as Record<string, TableFilterValue[]>));

// Deliberately mode-independent: it drives the search auto-expand, which should reveal matches
// in a nested tree whether the narrowing happened here or on the server. Local-only filtering
// is gated separately, on the `columnFilters` slice below.
const hasActiveQuery = computed(() => !!internalSearch.value
  || Object.keys(activeFilters.value).length > 0);

const columnDefs = computed(() => ([
  ...props.columns.map((column) => ({
    // Explicit id: an `accessorKey` containing a dot would be mangled into an id with `_`.
    id: column.key,
    accessorFn: (row: TableRow) => row[column.key],
    header: column.label ?? '',
    // Always sortable to the ENGINE. `column.sortable` only decides whether the header shows a
    // sort control — a caller can still drive `sort` (or v-model:sort) programmatically for a
    // column with no affordance, which is what this component has always allowed.
    enableSorting: true,
    // All filtering runs through QUERY_COLUMN_ID below.
    enableColumnFilter: false,
    // Defaults to 1, which would handle `undefined` BEFORE the comparator ever runs.
    sortUndefined: false,
    sortFn: (a: ZoneRow, b: ZoneRow) => compareValues(
      column.sortAccessor ? column.sortAccessor(a.original) : a.original[column.key],
      column.sortAccessor ? column.sortAccessor(b.original) : b.original[column.key],
    ),
  })),
  {
    id: QUERY_COLUMN_ID,
    accessorFn: () => null,
    header: '',
    enableSorting: false,
    filterFn: (row: ZoneRow, _columnId: string, value: TableQueryValue) => {
      // A row survives if it matches, OR if any ANCESTOR matches. The ancestor arm is what
      // makes a matching parent keep its whole subtree; `filterFromLeafRows` on its own only
      // handles the other direction (a matching descendant keeping its parents) and would
      // prune the non-matching children of a row that matched.
      let cursor: ZoneRow | undefined = row;

      while (cursor) {
        if (matchesSearch(props.columns, cursor.original, value.search)
          && matchesFilters(cursor.original, value.filters, props.filters)) {
          return true;
        }

        cursor = cursor.getParentRow?.();
      }

      return false;
    },
  },
]) as ZoneColumnDef[]);

const table = useTable({
  features,

  // Must stay a computed. The adapter unrefs each top-level option key once per change; a
  // plain `props.rows` would be snapshotted at construction and never update again.
  data: tableData,
  columns: columnDefs,

  getRowId: (row: TableRow) => String(row[props.rowKey]),
  getSubRows: (row: TableRow) => {
    const children = row[props.childrenKey];

    if (!Array.isArray(children)) {
      return undefined;
    }

    // TanStack eagerly walks the whole tree at construction and calls getRowId on every node,
    // so a nullish entry would throw straight out of mount(). Drop them instead.
    return (children as TableRow[]).filter((child) => child !== null && child !== undefined);
  },

  // Keep a parent whose DESCENDANT matches, keeping only the descendants that matched.
  filterFromLeafRows: true,
  // Children ride along on their parent's page, so getRowCount() counts TOP-LEVEL rows only.
  paginateExpandedRows: false,
  // This component owns page resets (they have to emit `update:page`). TanStack's own reset
  // fires in a microtask after the row model is read, emits nothing, and would race ours.
  autoResetAll: false,

  // Server mode renders `rows` verbatim: every stage short-circuits to its previous model.
  // `manualExpanding` is deliberately absent — expansion is always client-side, in both modes.
  manualFiltering: isServerMode,
  manualSorting: isServerMode,
  manualPagination: computed(() => isServerMode.value || !props.paginated),

  // NOTE: no `rowCount` option. It would only ever be meaningful in server mode, and there is
  // no way to conditionally omit an option here: a computed resolving to `undefined` does NOT
  // leave the option unset. Both `mergeProxy.get` and `flatMerge` skip undefined values and
  // fall through to the previous source — which still holds the RAW ComputedRef — so
  // `table.options.rowCount` ends up a ref object, `?? ` never fires, and `getRowCount()`
  // returns the ref instead of a number. The row count is computed in `totalItems` instead.

  // One computed for the whole state bag: nested refs are NOT unwrapped, so binding a ref per
  // slice would store the Ref object itself.
  state: computed(() => ({
    sorting: internalSort.value
      ? [{ id: internalSort.value.key, desc: internalSort.value.direction === 'desc' }]
      : [],

    columnFilters: (!isServerMode.value && hasActiveQuery.value)
      ? [{
        id: QUERY_COLUMN_ID,
        value: { search: internalSearch.value, filters: activeFilters.value },
      }]
      : [],

    // `true` means "everything expanded" — that is how the search auto-expand is expressed,
    // without touching the caller's `expandedKeys` (so no spurious update:expandedKeys).
    expanded: hasActiveQuery.value
      ? true
      : Object.fromEntries(internalExpanded.value.map((key) => [String(key), true])),

    pagination: {
      // TanStack is 0-based; this component's public API is 1-based.
      pageIndex: Math.max(0, internalPage.value - 1),
      pageSize: props.paginated ? (internalPerPage.value || 1) : Number.MAX_SAFE_INTEGER,
    },
  })),

  // Intentionally inert. Every mutation goes through this component's own setters (which emit
  // `update:*`); these exist only so the library's default state updaters can't write behind us.
  onSortingChange: () => {},
  onColumnFiltersChange: () => {},
  onExpandedChange: () => {},
  onPaginationChange: () => {},
});

// ── everything else ─────────────────────────────────────────────────────────────────────────────

const internalSelected = ref<TableSelection>(cloneSelection(props.selected));
// The search field's own value. Separate from `internalSearch` (the committed one) so typing
// stays instant while the commit — and any refetch behind it — waits out `searchDebounce`.
const searchInput = ref(props.search);
let searchTimer: ReturnType<typeof setTimeout> | null = null;
const filtersOpen = ref(false);
const internalLoading = ref(false);
// Monotonic id so a slow fetch that resolves after a newer one can't overwrite it.
let fetchToken = 0;

const isLoading = computed<boolean>(() => props.loading || internalLoading.value);

/** Everything not pinned into the toolbar, i.e. whatever the Filters trigger opens. */
const surfaceFilters = computed<TableFilter[]>(() => props.filters.filter((filter) => filter.placement !== 'inline'));

/**
 * Kept as an alias of `surfaceFilters`. `panelFilters` shipped as part of this component's
 * vocabulary and reads in the specs; "panel" now names one of two surfaces rather than
 * "not inline", so `surfaceFilters` is the honest name and this stays for compatibility.
 */
const panelFilters = computed<TableFilter[]>(() => surfaceFilters.value);

const inlineFilters = computed<TableFilter[]>(() => props.filters.filter((filter) => filter.placement === 'inline'));

/**
 * Which chrome the Filters trigger opens: the side drawer (the default) or the anchored
 * floating menu (`placement: 'panel'`).
 *
 * ONE surface per table, not one per filter, because there is one trigger and a button can
 * only open one thing. A table whose filters disagree resolves to the drawer — the default,
 * and the roomier of the two, so nothing declared for a popover is lost in a surface too
 * small for it. Declaring `placement` per filter is still how you choose (that is the API),
 * it just has to agree across the filters of a single table; only `'inline'` is genuinely
 * per-filter, since those render in the toolbar rather than behind the trigger.
 */
const filterSurface = computed<Exclude<TableFilterPlacement, 'inline'>>(() => {
  const allPanel = surfaceFilters.value.length > 0
    && surfaceFilters.value.every((filter) => filter.placement === 'panel');

  return allPanel ? 'panel' : 'drawer';
});

const hasFilterSurface = computed<boolean>(() => surfaceFilters.value.length > 0 || Boolean(slots.filters));

/**
 * Reading the filter bag. A thin wrapper over `./table/filters`, shared with FilterFields.vue
 * so the same stored value can never be read two ways.
 */
function isFilterActive(filter: TableFilter): boolean {
  return filterIsActive(internalFilters.value, filter);
}

// Surface filters only, so the badge always describes exactly what Reset would clear.
const activeFilterCount = computed<number>(() => surfaceFilters.value.filter((filter) => isFilterActive(filter)).length);

// The Zone spec caps the badge; a raw 3-digit count would blow out the trigger's corner.
const activeFilterBadge = computed<string>(() => (activeFilterCount.value > 99 ? '99+' : String(activeFilterCount.value)));

const queryState = computed<TableQueryState>(() => ({
  page: internalPage.value,
  perPage: internalPerPage.value,
  sort: internalSort.value,
  search: internalSearch.value,
  filters: activeFilters.value,
}));

// Watching the serialized state (rather than the object) keeps the fetcher from re-running
// when an unrelated re-render hands back a structurally identical object.
const queryKey = computed<string>(() => JSON.stringify(queryState.value));

/** The visible tree, flattened into render order by TanStack's expanded row model. */
const flatRows = computed<FlatRow[]>(() => {
  const counters = new Map<string, number>();

  return (table.getRowModel().rows as ZoneRow[]).map((row) => {
    // Our `index` is the position among RENDERED siblings. That is not `row.index`, which is
    // the position in the original data array and doesn't follow sorting or paging.
    const bucket = row.parentId ?? '';
    const index = counters.get(bucket) ?? 0;

    counters.set(bucket, index + 1);

    const hasChildren = row.getCanExpand();

    return {
      // The RAW key, not `row.id` — TanStack stringifies row ids and our public API
      // (update:selected, update:expandedKeys) emits whatever the caller put in the data.
      key: row.original[props.rowKey] as TableRowKey,
      row: row.original,
      level: row.depth,
      index,
      hasChildren,
      // Under the search auto-expand (`expanded: true`) getIsExpanded() is true for leaves too.
      expanded: hasChildren && row.getIsExpanded(),
    };
  });
});

/**
 * Total TOP-LEVEL rows behind the current page.
 *
 * Locally that is the filtered model's top-level `rows`. Deliberately NOT the pre-paginated
 * model: with `paginated: false`, `manualPagination` is true, which makes the expansion stage
 * flatten — so the pre-paginated model would count expanded children as items too.
 */
const totalItems = computed<number>(() => {
  if (isServerMode.value) {
    if (props.fetcher) {
      return fetchedTotal.value ?? fetchedRows.value.length;
    }

    return props.totalCount ?? props.rows.length;
  }

  return table.getFilteredRowModel().rows.length;
});

const pageCount = computed<number>(() => {
  if (!props.paginated) {
    return 1;
  }

  return Math.max(1, Math.ceil(totalItems.value / (internalPerPage.value || 1)));
});

function childrenOf(row: TableRow): TableRow[] {
  const children = row[props.childrenKey];

  return Array.isArray(children) ? (children as TableRow[]) : [];
}

const hasNestedRows = computed<boolean>(() => sourceRows.value.some((row) => childrenOf(row).length > 0));

const columnCount = computed<number>(() => props.columns.length + (props.selectable ? 1 : 0));

const showToolbar = computed<boolean>(() => {
  if (props.toolbar !== null) {
    return props.toolbar;
  }

  return props.searchable
    || props.filters.length > 0
    || !!slots['toolbar-actions']
    || !!slots.filters;
});

/** True while selection is the "all, minus except" shape rather than a list of keys. */
const isAllSelection = computed<boolean>(() => (
  !Array.isArray(internalSelected.value) && !!(internalSelected.value as TableCrossPageSelection)?.all
));

/** The exclusions in all-mode; empty in key-list mode. */
const exceptKeys = computed<TableRowKey[]>(() => (
  isAllSelection.value ? (internalSelected.value as TableCrossPageSelection).except : []
));

const selectedKeys = computed<TableRowKey[]>(() => (Array.isArray(internalSelected.value) ? internalSelected.value : []));

// ── selection ───────────────────────────────────────────────────────────────────────────────────
//
// Deliberately NOT TanStack's `rowSelectionFeature`. Its state is a Record keyed by STRINGIFIED
// row ids, while this component's public API is an array of the caller's raw keys (a number id
// must come back a number). Its "all page rows" helpers also count collapsed descendants, which
// is not what the header checkbox means here, and its toggle handler expects a native change
// event — ZoneCheckbox emits a boolean.

function canSelectRow(row: TableRow): boolean {
  return props.rowSelectable ? !!props.rowSelectable(row) : true;
}

function isSelected(key: TableRowKey): boolean {
  if (isAllSelection.value) {
    return !exceptKeys.value.includes(key);
  }

  return selectedKeys.value.includes(key);
}

/** Visible rows the user is actually allowed to pick — the basis for every bulk helper. */
const selectableRows = computed<FlatRow[]>(() => flatRows.value.filter((entry) => canSelectRow(entry.row)));

/**
 * How many rows "select all" would actually reach. The caller's `batchTotal` when they gave
 * one — only they can know the size of a server-side result set — otherwise the table's own
 * `totalItems`, which is exactly that number for a local table and for a server table that
 * reports `totalCount`.
 *
 * Defaulting rather than staying null is what makes "Select all (N)" show a real figure
 * instead of needing every caller to wire a prop (and, before this, hiding the affordance
 * entirely whenever they didn't).
 */
const effectiveBatchTotal = computed<number>(() => props.batchTotal ?? totalItems.value);

const selectedCount = computed<number>(() => {
  if (isAllSelection.value) {
    return Math.max(0, effectiveBatchTotal.value - exceptKeys.value.length);
  }

  return selectedKeys.value.length;
});

// Grouped alongside the pagination summary: these describe the same collection, so one of them
// reading "4821" while the other reads "4,821" looks like a bug.
const selectedCountText = computed<string>(() => formatCount(selectedCount.value));

const batchTotalText = computed<string>(() => formatCount(effectiveBatchTotal.value));

const showBatchBar = computed<boolean>(() => props.selectable && selectedCount.value > 0);

// Redundant under crossPageSelectable: the header checkbox already selects everything, so
// offering a second "select all" would be two controls for one action.
const canSelectAllAcrossPages = computed<boolean>(() => (
  !props.crossPageSelectable && effectiveBatchTotal.value > selectedCount.value
));

// Non-selectable rows are excluded from both, or a single vetoed row would make the header
// checkbox unreachable — `.every()` over ALL rows could then never be true.
const allVisibleSelected = computed<boolean>(() => {
  if (isAllSelection.value) {
    return exceptKeys.value.length === 0;
  }

  return selectableRows.value.length > 0 && selectableRows.value.every((entry) => isSelected(entry.key));
});

const selectAllIndeterminate = computed<boolean>(() => {
  if (isAllSelection.value) {
    return exceptKeys.value.length > 0;
  }

  return !allVisibleSelected.value && selectableRows.value.some((entry) => isSelected(entry.key));
});

/** Nothing on this page can be picked, so the header checkbox has nothing to act on. */
const selectAllDisabled = computed<boolean>(() => !isAllSelection.value && selectableRows.value.length === 0);

// `overflow-y-auto` only once a height is capped. Without the cap the container is as tall as
// its content and the declaration is inert; with it, the body scrolls under a sticky header.
const scrollContainerClasses = computed<string>(() => [
  'no-scrollbar relative overflow-x-auto',
  props.maxHeight ? 'overflow-y-auto' : '',
].filter(Boolean).join(' '));

/**
 * Names of every slot this table was given, so the parts can relay the DYNAMIC ones
 * (`cell-KEY`, `header-KEY`) that cannot be declared statically. Harmless that each part is
 * offered slots it does not render — an unused slot renders nothing.
 */
const forwardedSlots = computed<string[]>(() => Object.keys(slots));

/** The only state the pure class builders in `./table/cells` need. */
const stickyOptions = computed<StickyOptions>(() => ({ stickyFirstColumn: props.stickyFirstColumn, selectable: props.selectable }));

// ── horizontal scrollbar ────────────────────────────────────────────────────────────────────────
//
// The bar itself lives in table/Scrollbar.vue, which owns the measuring and the drag. Kept
// here: the container element it reports on, and this delegating `syncScrollbar` — the
// `@scroll` handler on the container calls it, and it is the documented way to force a
// re-measure.

/** Handed to table/Scrollbar.vue as a getter, so it always reads the CURRENT element. */
function getScrollContainer(): HTMLElement | null {
  return scrollContainer.value ?? null;
}

/** Returns Scrollbar's promise so a forced re-measure can be awaited — see `sync()` there. */
function syncScrollbar(): Promise<void> | undefined {
  return scrollbar.value?.sync();
}

/**
 * Any scroll dismisses this table's own overlays, which would otherwise hang detached from the
 * control that opened them. Scrolling INSIDE an overlay is exempt — a long filter list has to
 * stay open while you scroll it. Overlays teleport to #zone-teleport, so that subtree is the
 * test. Caller-rendered overlays inside cells are not our business; listen for `scroll` for those.
 */
function onDocumentScroll(event: Event) {
  const target = event.target as Node | null;

  if (target && (target as Element).nodeType === 1 && (target as Element).closest?.('#zone-teleport')) {
    return;
  }

  // Only the POPOVER surface. Scroll-dismissal is there because an anchored panel would
  // otherwise drift away from the trigger it points at; a drawer is pinned to the viewport
  // edge and pointing at nothing, and it is modal with the body scroll locked, so closing it
  // on a stray scroll event would just throw away half-entered filters.
  if (filterSurface.value === 'panel') {
    filtersOpen.value = false;
  }

  batchBar.value?.closeMenu();
  emit('scroll', event);
}

// ── state setters ───────────────────────────────────────────────────────────────────────────────

function setPage(value: number) {
  const next = Math.max(1, value);

  if (next === internalPage.value) {
    return;
  }

  internalPage.value = next;
  emit('update:page', next);
}

function setPerPage(value: unknown) {
  const next = Number(value);

  if (!next || next === internalPerPage.value) {
    return;
  }

  internalPerPage.value = next;
  emit('update:perPage', next);
  setPage(1);
}

// ── sorting ─────────────────────────────────────────────────────────────────────────────────────

/**
 * Single-column sort, cycling asc → desc → off. Deliberately NOT TanStack's
 * `getToggleSortingHandler()`: that cycle flips direction based on the column's data type,
 * enables multi-sort on shift-click, and never produces the `null` this component emits.
 */
function toggleSort(column: TableColumn) {
  const current = internalSort.value;
  let next: TableSort | null = { key: column.key, direction: 'asc' };

  if (current?.key === column.key) {
    next = current.direction === 'asc' ? { key: column.key, direction: 'desc' } : null;
  }

  internalSort.value = next;
  emit('update:sort', next);
  setPage(1);
}

function cancelSearchDebounce() {
  if (searchTimer) {
    clearTimeout(searchTimer);
    searchTimer = null;
  }
}

function setSearch(value: string) {
  if (value === internalSearch.value) {
    return;
  }

  internalSearch.value = value;
  emit('update:search', value);
  setPage(1);
}

/** What the field calls on every keystroke: echo immediately, commit on the debounce. */
function onSearchInput(value: string) {
  searchInput.value = value;
  cancelSearchDebounce();

  if (!props.searchDebounce) {
    setSearch(value);

    return;
  }

  searchTimer = setTimeout(() => {
    searchTimer = null;
    setSearch(value);
  }, props.searchDebounce);
}

function setFilter(key: string, values: unknown) {
  const next = { ...internalFilters.value, [key]: (values as TableFilterValue[]) ?? [] };

  internalFilters.value = next;
  emit('update:filterValues', next);
  setPage(1);
}

/**
 * Resets the PANEL only. Inline filters are visible in the toolbar, so wiping them from behind
 * a popover the user has open would be invisible action at a distance.
 */
function clearFilters() {
  const next = { ...internalFilters.value };

  panelFilters.value.forEach((filter) => { delete next[filter.key]; });

  internalFilters.value = next;
  emit('update:filterValues', next);
  setPage(1);
}

function updateSelection(next: TableSelection) {
  internalSelected.value = next;
  emit('update:selected', next);
}

function toggleRow(key: TableRowKey, checked: boolean) {
  // In all-mode a row is deselected by ADDING it to the exclusions, and vice versa.
  if (isAllSelection.value) {
    const except = checked
      ? exceptKeys.value.filter((candidate) => candidate !== key)
      : [...exceptKeys.value, key];

    updateSelection({ all: true, except });

    return;
  }

  if (checked) {
    if (!isSelected(key)) {
      updateSelection([...selectedKeys.value, key]);
    }

    return;
  }

  updateSelection(selectedKeys.value.filter((candidate) => candidate !== key));
}

function toggleAll(checked: boolean) {
  if (props.crossPageSelectable) {
    // Rows this page can see but the user may not pick start out excluded. Rows on pages we
    // have never fetched can't be vetoed from here — the server has to apply the same rule.
    const except = checked
      ? flatRows.value.filter((entry) => !canSelectRow(entry.row)).map((entry) => entry.key)
      : [];

    updateSelection(checked ? { all: true, except } : []);

    return;
  }

  const visible = selectableRows.value.map((entry) => entry.key);

  if (!checked) {
    updateSelection(selectedKeys.value.filter((key) => !visible.includes(key)));

    return;
  }

  const next = [...selectedKeys.value];

  visible.forEach((key) => {
    if (!next.includes(key)) {
      next.push(key);
    }
  });

  updateSelection(next);
}

function clearSelection() {
  updateSelection([]);
}

/**
 * Local mode can genuinely select every row it holds. Server mode can't — it only has the
 * current page — so it just announces the intent and the caller decides what "all" means.
 */
function selectAllAcrossPages() {
  emit('select-all-across-pages');

  if (isServerMode.value) {
    return;
  }

  // Every row surviving the current search/filters, at every depth — deliberately NOT the raw
  // `rows`, so "select all" never reaches rows the user has just filtered away.
  updateSelection(
    (table.getFilteredRowModel().flatRows as ZoneRow[])
      .filter((row) => canSelectRow(row.original))
      .map((row) => row.original[props.rowKey] as TableRowKey),
  );
}

function runBatchAction(action: TableBatchAction) {
  emit('batch-action', { action, selected: cloneSelection(internalSelected.value) });
}

// ── expansion ───────────────────────────────────────────────────────────────────────────────────

// The expanded/collapsed TREE is TanStack's job; this only owns the caller-facing array of raw
// keys, which the `expanded` state computed derives its string-keyed map from.
function toggleExpanded(key: TableRowKey) {
  const next = internalExpanded.value.includes(key)
    ? internalExpanded.value.filter((candidate) => candidate !== key)
    : [...internalExpanded.value, key];

  internalExpanded.value = next;
  emit('update:expandedKeys', next);
}

// ── server fetching ─────────────────────────────────────────────────────────────────────────────

async function runFetch() {
  if (!props.fetcher) {
    return;
  }

  fetchToken += 1;
  const token = fetchToken;

  internalLoading.value = true;

  try {
    const result = await props.fetcher(queryState.value);

    if (token !== fetchToken) {
      return;
    }

    if (Array.isArray(result)) {
      fetchedRows.value = result;
      fetchedTotal.value = null;
    } else if (result && typeof result === 'object') {
      fetchedRows.value = result.rows ?? [];
      fetchedTotal.value = typeof result.totalCount === 'number' ? result.totalCount : null;
    }
  } catch (error) {
    emit('fetch-error', error);
  } finally {
    if (token === fetchToken) {
      internalLoading.value = false;
    }
  }
}

// ── classes ─────────────────────────────────────────────────────────────────────────────────────
//
// A thin wrapper over `./table/cells`, which holds the actual rules. `stickyOptions` is the one
// piece of state the pure builders need — passing it through here means the header and the body
// cannot be given different geometry by accident.

function stickyColumnClasses(columnIndex: number, region: StickyRegion = 'body'): string {
  return stickyColumnClassesFor(columnIndex, stickyOptions.value, region);
}

// ── prop → state sync ───────────────────────────────────────────────────────────────────────────

watch(() => props.page, (value) => {
  internalPage.value = value;
});

watch(() => props.perPage, (value) => {
  internalPerPage.value = value;
});

watch(() => props.sort, (value) => {
  internalSort.value = value;
});

// A push from the caller wins over whatever the user was mid-way through typing.
watch(() => props.search, (value) => {
  cancelSearchDebounce();
  internalSearch.value = value;
  searchInput.value = value;
});

watch(() => props.filterValues, (value) => {
  internalFilters.value = { ...value };
}, { deep: true });

watch(() => props.selected, (value) => {
  internalSelected.value = cloneSelection(value);
}, { deep: true });

watch(() => props.expandedKeys, (value) => {
  internalExpanded.value = [...value];
}, { deep: true });

// A change the user made. The starting state is never announced: it is the one the caller passed
// in (in an Inertia page, the one the server already answered).
watch(queryKey, () => {
  emit('query-change', queryState.value);

  if (isServerMode.value && props.fetcher) {
    runFetch();
  }
});

// A local narrowing (search/filter/per-page) can leave us past the last page.
watch(pageCount, (value) => {
  if (internalPage.value > value) {
    setPage(value);
  }
});

onMounted(() => {
  // The scrollbar can only start observing once its container exists, and a child mounts before
  // its parent — so it is started from here, exactly where `initScrollbar()` used to be called.
  scrollbar.value?.observe();

  // Capture phase, on document: `scroll` does not bubble, so a listener anywhere else would miss
  // scrolling inside this table's own overflow container — or any ancestor's.
  document.addEventListener('scroll', onDocumentScroll, true);

  if (isServerMode.value && props.fetcher) {
    runFetch();
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('scroll', onDocumentScroll, true);
  cancelSearchDebounce();
  scrollbar.value?.teardown();
});

// The thumb is sized from the container's real geometry, so it has to be recomputed after any
// render that can change the row count or the column widths. Deferred to a frame so a burst of
// updates (a search that re-renders on every keystroke) costs ONE forced reflow, not one each.
onUpdated(() => {
  scrollbar.value?.scheduleSync();
});

defineExpose({
  /** Forces a re-measure of the horizontal scrollbar from outside. */
  syncScrollbar,
  stickyColumnClasses,
  /**
   * Server mode: runs the `fetcher` again with the current query state, for a page that changed
   * the data behind the table (an archive, a delete) or wants a Retry after `fetch-error`.
   * A no-op without a fetcher.
   */
  reload: runFetch,
});
</script>

<i18n lang="json">
{
  "en": {
    "search_placeholder": "Search"
  }
}
</i18n>
